/**
 * Experiências especiais (13/10/2026): dinâmicas de data marcada que a Eva
 * cria no portal do mantenedor e libera para as marcas que escolhe. A
 * primeira é a Onda de Luz — 15 de outubro, Dia Internacional de
 * Sensibilização para a Perda Gestacional e Neonatal: às 19h, no fuso de
 * cada família, uma vela acesa, e a luz "passa" pelo mundo.
 *
 * A experiência em si é código (o tipo); o que é dado fica aqui: a data, o
 * horário local, as marcas, se o memorial está no ar. O memorial abre por um
 * link com hash (`/eventos/<hash>`): não é página pública, é o que se mostra
 * na live.
 */
import { z } from 'zod';
import { brandIdField } from './brand';

export const TipoDeExperienciaEnum = z.enum(['ONDA_DE_LUZ']);
export type TipoDeExperiencia = z.infer<typeof TipoDeExperienciaEnum>;

/**
 * Uma busca do mural no Instagram (Apify) e quanto ela custou. O custo é o
 * `usageTotalUsd` da execução, relido até ela assentar.
 */
export const BuscaDoMuralSchema = z.object({
  runId: z.string(),
  /** ISO 8601. Texto, e não data: data dentro de lista volta do Firestore como Timestamp. */
  iniciadaEm: z.string(),
  custoUsd: z.number().nullable().default(null),
  encontrados: z.number().int().default(0),
  novos: z.number().int().default(0),
  /** ISO 8601, quando a importação terminou. Sem ela, a busca está em andamento. */
  concluidaEm: z.string().nullable().default(null),
  /** Quantos posts a busca pede (hashtags × limite): a régua do progresso. */
  alvo: z.number().int().nullable().default(null),
  /**
   * Os posts cuja imagem não baixou. Ficam aqui, e não na tela, para a busca
   * poder ser retomada depois de recarregar a página sem tentar de novo.
   */
  falharam: z.array(z.string()).default([]),
});
export type BuscaDoMural = z.infer<typeof BuscaDoMuralSchema>;

export const ExperienciaEspecialSchema = z.object({
  id: z.string(),
  tipo: TipoDeExperienciaEnum,
  nome: z.string().trim().min(1).max(80),
  /** O dia da experiência, `AAAA-MM-DD`. Pode ser outro para testar (ex.: a véspera). */
  data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** A hora local em que cada vela brilha, `HH:MM`, no fuso de quem acendeu. */
  horaLocal: z.string().regex(/^\d{2}:\d{2}$/).default('19:00'),
  /** As marcas cujas pacientes participam. */
  marcas: z.array(z.string()).default([]),
  /**
   * As jornadas que participam (o `userType` da paciente; quem acompanha
   * conta pela jornada de quem acompanha). Lista vazia: todas. O padrão é
   * "Perdi meu bebê" — a Onda de Luz é de quem perdeu um bebê (14/10/2026),
   * e é ele que vale para as experiências gravadas antes do campo existir.
   */
  jornadas: z.array(z.string()).default(['MAE_DE_ANJO']),
  /** O link do memorial abre? Desligado, responde como se não existisse. */
  memorialNoAr: z.boolean().default(false),
  /** Nome e mensagem esperam aprovação antes de aparecer no memorial? */
  moderarAntes: z.boolean().default(true),
  /** As hashtags que o mural busca no Instagram, sem o `#`. */
  hashtags: z.array(z.string().trim().min(1).max(60)).default([]),
  /** As buscas do mural feitas até aqui, com o custo de cada uma (13/10/2026). */
  buscasDoMural: z.array(BuscaDoMuralSchema).default([]),
  /** O segredo do link do memorial. */
  hash: z.string().min(16),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ExperienciaEspecial = z.infer<typeof ExperienciaEspecialSchema>;

export const DadosDaExperienciaSchema = ExperienciaEspecialSchema.pick({
  tipo: true,
  nome: true,
  data: true,
  horaLocal: true,
  marcas: true,
  jornadas: true,
  memorialNoAr: true,
  moderarAntes: true,
  hashtags: true,
});
export type DadosDaExperiencia = z.infer<typeof DadosDaExperienciaSchema>;

export const ModeracaoEnum = z.enum(['PENDENTE', 'APROVADA', 'RECUSADA']);
export type Moderacao = z.infer<typeof ModeracaoEnum>;

export const NOME_DO_BEBE_MAX = 60;
export const MENSAGEM_DA_VELA_MAX = 280;

/**
 * Uma vela acesa no app. A cidade é obrigatória — é ela que põe a vela no
 * mapa —; o nome do bebê e a mensagem, não. Nome e mensagem só aparecem no
 * memorial se a família autorizou (`publicar`) e, com moderação, depois de
 * aprovados. O ponto de luz no mapa é anônimo e aparece sempre.
 */
export const VelaSchema = z.object({
  id: z.string(),
  experienciaId: z.string(),
  brandId: brandIdField(),
  userId: z.string(),
  uf: z.string().length(2),
  cidade: z.string().trim().min(1).max(80),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  /** O fuso de quem acendeu (IANA, do aparelho): a vela brilha às `horaLocal` dele. */
  fuso: z.string().min(1).max(60),
  /** Quando ela brilha no mapa: a hora local do fuso no dia da experiência, ou quando foi acesa, se depois. */
  brilhaEm: z.coerce.date(),
  nomeDoBebe: z.string().trim().max(NOME_DO_BEBE_MAX).nullable().optional(),
  mensagem: z.string().trim().max(MENSAGEM_DA_VELA_MAX).nullable().optional(),
  publicar: z.boolean().default(false),
  moderacao: ModeracaoEnum.default('PENDENTE'),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Vela = z.infer<typeof VelaSchema>;

/** Uma foto do mural, trazida do Instagram pelas hashtags e copiada para o bucket. */
export const ItemDoMuralSchema = z.object({
  id: z.string(),
  experienciaId: z.string(),
  origem: z.enum(['INSTAGRAM']),
  /** O id do post na origem: buscar de novo não duplica. */
  postId: z.string(),
  link: z.string().url().nullable().optional(),
  imagemUrl: z.string().url(),
  autor: z.string().max(80).nullable().optional(),
  legenda: z.string().max(2200).nullable().optional(),
  postadoEm: z.coerce.date().nullable().optional(),
  status: ModeracaoEnum.default('PENDENTE'),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ItemDoMural = z.infer<typeof ItemDoMuralSchema>;
